import { useCurrencyCodes } from '../../lib/use-dict.ts';
// 共用銀行帳戶輸入欄位 (2026-08-08)。
//
// 三套帳戶 (記帳主體 / 投資者收款 / 員工薪轉) 本來各自長出一份表單, 銀行/分行/戶名/
// 帳號有三種切法、三種驗證強度。 這裡把「輸入格式」統一成一份 —— 但**不**碰各自的
// 安全模型 (加密與否、審核流) —— 那是三種錢流方向的本質差異, 不該為了一致而拉平:
//   · 記帳主體帳戶: 公司自己的收款帳號, 要完整印在通知信/Invoice → 明文, 不加密
//   · 投資者收款 / 員工薪轉: 別人的私人帳號, 匯錢**出去** → 加密 + 遮罩 + (投資者)審核
//
// 用法: 呼叫端持有 value/onChange, 這裡只負責畫欄位 + 統一驗證格式。
import { Field, inputClass } from './Field.tsx';

export interface BankFieldsValue {
  currency: string;
  bank_name: string;
  branch_name: string;
  account_holder_name: string;
  account_number: string;
}

// 幣別改讀字典 (2026-08-21 字典審計) — 見 use-dict.ts useCurrencyCodes。
// ⚠ lp-portal 那份 BankFields 仍是寫死清單 — 投資人入口的 API 沒有 master-data
//   端點 (internal-only), 讀不到字典; 那邊要跟著字典就得另開 LP 端點, 目前不做。

/** 帳號正規化: 去空白與連字號, 只留數字 (台灣銀行帳號都是純數字)。 存前跑一次,
 *  三套一致 → 對帳/顯示不會因為有人打了 "012-345" 有人打 "012345" 而對不起來。 */
export function normalizeAccountNumber(raw: string): string {
  return raw.replace(/[\s-]/g, '');
}

/** 帳號是否合法 (正規化後 4~30 碼數字)。 */
export function isValidAccountNumber(raw: string): boolean {
  const n = normalizeAccountNumber(raw);
  return /^\d{4,30}$/.test(n);
}

export function BankFields({
  value,
  onChange,
  showCurrency = true,
  accountHint,
  disabled = false,
  required = true,
  accountRequired,
  accountPlaceholder,
}: {
  value: BankFieldsValue;
  onChange: (patch: Partial<BankFieldsValue>) => void;
  /** 員工薪轉只有 TWD → 隱藏幣別欄。 */
  showCurrency?: boolean;
  /** 帳號欄的提示 (加密的那兩套寫「送出即加密」)。 */
  accountHint?: string;
  disabled?: boolean;
  /**
   * 2026-08-09: 廠商可以先建、之後再補匯款資訊 —— 那三格標必填星號是在說謊
   * (呼叫端的驗證也沒真的擋)。 其餘三套 (記帳主體/投資者/員工) 維持必填。
   */
  required?: boolean;
  /**
   * 2026-08-25: 編輯已存在的加密帳號時, 帳號欄是「留空 = 不變更」→ 那一格不該標必填,
   * 但銀行 / 戶名仍然必填。 省略時跟著 `required`。
   */
  accountRequired?: boolean;
  /** 編輯時用來顯示現有遮罩 (「留空不變，目前 ****1234」)。 */
  accountPlaceholder?: string;
}) {
  const currencies = useCurrencyCodes();
  const acctBad = value.account_number.trim() !== '' && !isValidAccountNumber(value.account_number);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {showCurrency && (
        <Field label="幣別" required={required}>
          <select
            value={value.currency}
            onChange={(e) => onChange({ currency: e.target.value })}
            disabled={disabled}
            className={inputClass}
          >
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="銀行" required={required}>
        <input
          value={value.bank_name}
          onChange={(e) => onChange({ bank_name: e.target.value })}
          maxLength={120}
          disabled={disabled}
          placeholder="例：範例銀行 (000)"
          className={inputClass}
        />
      </Field>
      <Field label="分行">
        <input
          value={value.branch_name}
          onChange={(e) => onChange({ branch_name: e.target.value })}
          maxLength={120}
          disabled={disabled}
          placeholder="例：城中分行"
          className={inputClass}
        />
      </Field>
      <Field label="戶名" required={required}>
        <input
          value={value.account_holder_name}
          onChange={(e) => onChange({ account_holder_name: e.target.value })}
          maxLength={120}
          disabled={disabled}
          className={inputClass}
        />
      </Field>
      <Field
        label="帳號"
        required={accountRequired ?? required}
        hint={accountHint}
        error={acctBad ? { message: '帳號格式：4~30 碼數字' } : undefined}
      >
        <input
          value={value.account_number}
          onChange={(e) => onChange({ account_number: e.target.value })}
          maxLength={40}
          disabled={disabled}
          inputMode="numeric"
          placeholder={accountPlaceholder ?? '僅數字，可省略連字號'}
          className={inputClass}
        />
      </Field>
    </div>
  );
}
